/* eslint-disable react-refresh/only-export-components -- provider + hook pattern requires co-location */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: ResolvedTheme;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(DARK_QUERY).matches;
}

function readStoredTheme(fallback: Theme): Theme {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Ignore storage access failures and fall back to the default theme.
  }
  return fallback;
}

// Keep the `.dark` class on <html> in sync with the resolved theme. An inline
// bootstrap in index.html applies this before first paint (no FOUC); this keeps
// it current as the user toggles.
function applyResolvedTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

export function ThemeProvider({
  children,
  defaultTheme = 'light',
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
}): React.ReactElement {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme(defaultTheme));
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Derived during render — no effect setState — so it's always consistent with theme + OS.
  const resolvedTheme: ResolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  // Apply the resolved theme to <html> before paint (useLayoutEffect, not useEffect) so a runtime
  // toggle flips the `.dark` token class in the same frame the components re-render — no one-frame
  // flash. Initial load is already handled pre-paint by the inline bootstrap in index.html.
  useLayoutEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Track the OS preference unconditionally (not only in 'system' mode) so `systemDark` always
  // mirrors the OS — switching into 'system' then resolves correctly with no stale-state window.
  // In explicit light/dark an OS flip re-renders only this provider; the memoized value below keeps
  // the context referentially stable when resolvedTheme is unchanged, so consumers don't re-render.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (): void => setSystemDark(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Persist only on an explicit user choice — never on mount — so a first-time visitor isn't
  // locked into today's default and `defaultTheme` can still change for them later.
  const setTheme = useCallback((next: Theme): void => {
    setThemeState(next);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore storage failures; the in-memory theme still applies for this session.
    }
  }, []);

  // Memoized so consumers re-render only when the resolved theme (or the stable setter) actually
  // changes — not on every provider render (e.g. an OS flip while in explicit light/dark, see above).
  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, resolvedTheme }),
    [theme, setTheme, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
